import { State, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';

import { GOAL_PLAYER_EVENT_DEFINITION_NAME, computeCountUpGameTime, computeElapsedPeriodTime, computeLiveScore, computeRegulationOffset, formatOfficialPeriodTime } from './scoreboard.utils';

import { StateSliceName } from 'src/app/states/store.models';
import { EaVideoPlayerState } from 'src/app/containers/ea-video-player/ea-video-player.state';
import { GameState } from 'src/app/states/game/game.state';
import { IGamePeriod, IGameScore, IVidParam } from 'src/app/states/game/game.models';
import { EventState } from 'src/app/states/event/event.state';
import { SportState } from 'src/app/states/sport/sport.state';
import { ClockImpactType, IGameEvent, IGameEventDefinition, IPlayerEvent, IPlayerEventDefinition } from 'src/app/states/event/event.models';

/**
 * Derived game state (game clock and running score) at the current video position.
 *
 * This slice holds no data of its own: it is a home for selectors that translate the video
 * player's playhead (`EaVideoPlayerState`) into game semantics using the game/event/sport states.
 * Keeping it separate from the video player container lets any route that embeds `<ea-video-player>`
 * (eventor today; player-shift / time-tracker in the future) reuse the current video time, official
 * period clock, and live score.
 */
@Injectable()
@State<Record<string, never>>({
    name: StateSliceName.SCOREBOARD,
    defaults: {}
})
export class ScoreboardState {
    /**
     * Returns the absolute video position in seconds across the entire game.
     *
     * For continuous videos, the playhead time (currentVideoTimeSeconds) is already absolute.
     * For split videos (different file per period), the playhead time is relative to that period,
     * so we add selectedPeriodStartVideoSeconds to get the absolute time.
     */
    @Selector([
        EaVideoPlayerState.selectors.currentVideoTimeSeconds,
        EaVideoPlayerState.selectedPeriodStartVideoSeconds,
        GameState.selectors.vidParams
    ])
    public static absoluteVideoTimeSeconds(
        currentVideoTimeSeconds: number,
        selectedPeriodStartVideoSeconds: number | undefined,
        vidParams: IVidParam[] | undefined
    ): number {
        const isContinuous = !vidParams || vidParams.length <= 1 || vidParams.every(param => param.filename === vidParams[0].filename);
        if (isContinuous) {
            return currentVideoTimeSeconds;
        }

        return currentVideoTimeSeconds + (selectedPeriodStartVideoSeconds ?? 0);
    }

    /**
     * Returns the official game clock for the selected period at the current video position,
     * faithfully mirroring the legacy scoreboard controller (`scoreboardcontroller.js.php`).
     *
     * Both sport families anchor the elapsed period time to the most recent clock-impacting game
     * event (see `computeElapsedPeriodTime`): the clock does not advance linearly with the video
     * because it stops during whistles/stoppages while the video keeps rolling. Event frames and
     * the raw video playhead (`currentVideoTimeSeconds`) share the same basis (the same value the
     * video seeks to when an event is selected), so the raw playhead is used directly.
     *
     * Countdown sports (e.g. hockey): displayed as `periodLength - elapsed` (`HH:MM:SS`).
     *
     * Count-up sports (e.g. soccer): the official clock carries over between periods at the fixed
     * regulation boundary regardless of stoppage time actually played (the second half starts at
     * 45:00), so the clock is `(period - 1) * 45:00 + elapsed`, matching the legacy `getGameTime`
     * (`(period - 1) * fullPeriodLength + periodTime`). Displayed as total minutes (`MM:SS.cc`,
     * e.g. `83:53.00`).
     */
    // eslint-disable-next-line complexity
    @Selector([
        EaVideoPlayerState.selectors.currentVideoTimeSeconds,
        EaVideoPlayerState.vidParamForSelectedPeriod,
        GameState.selectors.selectedPeriod,
        GameState.selectors.periods,
        EventState.selectors.gameEvents,
        EventState.selectors.gameEventDefinitions,
        EventState.selectors.playerEvents,
        SportState.gameClockGoesDown
    ])
    public static currentPeriodTime(
        currentVideoTimeSeconds: number,
        vidParam: IVidParam | undefined,
        selectedPeriod: string | undefined,
        periods: Record<string, IGamePeriod> | null,
        gameEvents: IGameEvent[] | undefined,
        gameEventDefinitions: IGameEventDefinition[] | undefined,
        playerEvents: IPlayerEvent[] | undefined,
        gameClockGoesDown: boolean
    ): string {
        const period = selectedPeriod ?? '1';
        const periodNumber = parseInt(period, 10);
        const periodLength = periods?.[period]?.lengths.actual ?? 0;
        const frameRate = vidParam?.frameRate ?? 0;
        // Count-up clocks carry over at each period's regulation boundary (2nd half -> 45:00), never
        // the actual recorded length, which includes stoppage/halftime and would over-count.
        const regulationOffset = computeRegulationOffset(periods, periodNumber);

        if (frameRate <= 0) {
            return formatOfficialPeriodTime(gameClockGoesDown ? periodLength : regulationOffset, periodLength, gameClockGoesDown);
        }

        const currentFrame = currentVideoTimeSeconds * frameRate;

        // Count-up sports (soccer): the game (clock) events carry no usable time in the v3 feed, so the
        // clock is derived from the dense, correctly-stamped player-event `gameTime` (see helper).
        if (!gameClockGoesDown) {
            const gameTime = computeCountUpGameTime(currentFrame, frameRate, periodNumber, playerEvents ?? [], regulationOffset);
            return formatOfficialPeriodTime(gameTime, periodLength, false);
        }

        // TODO: Verify the countdown (hockey) clock against a real v3 game. The count-up (soccer) path
        // was moved off game (clock) events because they carry null `periodTime`/`gameTime` and frames
        // on a different basis than the video. This countdown path still anchors to those clock events,
        // so if hockey's feed has the same unusable clock-event data it will need the player-event
        // approach (see `computeCountUpGameTime`) too.
        const clockImpactByDefId: Record<string, ClockImpactType> = {};
        for (const definition of gameEventDefinitions ?? []) {
            clockImpactByDefId[definition.id] = definition.clockImpact;
        }

        const elapsed = computeElapsedPeriodTime(currentFrame, frameRate, periodNumber, gameEvents ?? [], clockImpactByDefId);

        return formatOfficialPeriodTime(periodLength - elapsed, periodLength);
    }

    /**
     * Returns the running score keyed by team ID at the current video position.
     *
     * Completed periods (those before the loaded one) contribute their full `periodScore`, while the
     * loaded period is scored live by counting goal player events the playhead has reached (see
     * `computeLiveScore`). Player events are only loaded for the current period, which is why prior
     * periods rely on the game's `periodScore` rather than the event feed.
     */
    // eslint-disable-next-line complexity
    @Selector([
        ScoreboardState.absoluteVideoTimeSeconds,
        EaVideoPlayerState.vidParamForSelectedPeriod,
        GameState.selectors.selectedPeriod,
        GameState.selectors.scores,
        EventState.selectors.playerEvents,
        EventState.selectors.playerEventDefinitions
    ])
    public static currentScore(
        absoluteVideoTimeSeconds: number,
        vidParam: IVidParam | undefined,
        selectedPeriod: string | undefined,
        scores: IGameScore | null,
        playerEvents: IPlayerEvent[] | undefined,
        playerEventDefinitions: IPlayerEventDefinition[] | undefined
    ): Record<string, number> {
        const period = parseInt(selectedPeriod ?? '1', 10);
        const frameRate = vidParam?.frameRate ?? 0;
        const currentFrame = frameRate > 0 ? absoluteVideoTimeSeconds * frameRate : 0;

        const goalDefinitionIds = new Set<string>();
        for (const definition of playerEventDefinitions ?? []) {
            if (definition.name?.toLowerCase() === GOAL_PLAYER_EVENT_DEFINITION_NAME) {
                goalDefinitionIds.add(definition.id);
            }
        }

        return computeLiveScore(
            scores?.periodScore ?? null,
            period,
            currentFrame,
            playerEvents ?? [],
            goalDefinitionIds
        );
    }
}
